/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useRef, useState } from 'react';

import AiManagementDatetimePicker from 'components/atoms/ai_management_datetime_picker';
import { errorMessageOf } from 'hooks/useDocumentRequirements';
import { Container } from './styles';

const MAX_BYTES = 50 * 1024 * 1024;

export interface NewDocumentFile {
  file: File;
  name: string;
  expiresOn?: string;
}

interface AiManagementAddDocumentPaneProps {
  requirementTitle: string;
  onAdd: (document: NewDocumentFile) => Promise<unknown>;
  onClose: () => void;
}

const AiManagementAddDocumentPane: React.FC<AiManagementAddDocumentPaneProps> = ({
  requirementTitle,
  onAdd,
  onClose,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [name, setName] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const selectFile = (selected?: File) => {
    if (!selected) return;
    if (selected.size > MAX_BYTES) {
      setErrorMessage('O arquivo deve ter no máximo 50 MB.');
      return;
    }
    setFile(selected);
    setErrorMessage('');
    if (!name.trim()) setName(selected.name.replace(/\.[^.]+$/, ''));
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    selectFile(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setErrorMessage('Selecione um arquivo para adicionar.');
      return;
    }

    setErrorMessage('');
    setSaving(true);
    onAdd({ file, name: name.trim() || file.name, expiresOn: expiresOn || undefined })
      .then(onClose)
      .catch((err) => {
        setErrorMessage(errorMessageOf(err, 'Não foi possível adicionar o documento.'));
        setSaving(false);
      });
  };

  return (
    <Container className="d-flex flex-column h-100" onSubmit={handleSubmit}>
      <div className="flex-grow-1 overflow-auto p-4">
        <div className="d-flex align-items-start justify-content-between mb-1">
          <h4 className="mb-0">Adicionar documento</h4>
          <button type="button" className="close ml-3" aria-label="Fechar" onClick={onClose}>
            <i className="material-icons">close</i>
          </button>
        </div>
        <small className="requirement-name d-block text-muted mb-4">{requirementTitle}</small>

        <button
          type="button"
          className={`upload-dropzone btn btn-block d-flex align-items-center justify-content-center mb-4 ${
            dragging ? 'is-dragging' : ''
          }`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <i className="material-icons mr-2">{file ? 'insert_drive_file' : 'file_upload'}</i>
          <span className="text-truncate">
            {file ? file.name : 'Faça o upload do seu arquivo aqui'}
          </span>
        </button>
        <input
          ref={inputRef}
          type="file"
          className="d-none"
          onChange={(event) => selectFile(event.target.files?.[0])}
        />

        <div className="form-group">
          <label htmlFor="add-document-nome">Nome do documento</label>
          <input
            id="add-document-nome"
            className="form-control"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="add-document-vencimento">Vencimento</label>
          <AiManagementDatetimePicker
            id="add-document-vencimento"
            value={expiresOn}
            onChange={setExpiresOn}
            withTime={false}
          />
        </div>

        {errorMessage && <div className="alert alert-danger mt-3 mb-0">{errorMessage}</div>}
      </div>

      <div className="d-flex justify-content-end border-top p-3">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Adicionando...' : 'Adicionar documento'}
        </button>
      </div>
    </Container>
  );
};

export default AiManagementAddDocumentPane;
